import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DataService } from '../../Service/data.service';
import {
  GlobalSearchAdvancedResponse,
  GlobalSearchGroup,
  GlobalSearchResponse,
  GlobalSearchSavedItem,
  GlobalSearchSuggestion
} from './global-search.models';
import { filterGlobalSearchPages } from './global-search-pages.catalog';

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly recentKey = 'ps.globalSearch.recent';

  constructor(private dataService: DataService) {}

  search(q: string): Observable<GlobalSearchResponse> {
    const token = this.dataService.getUserToken();
    const body: any = { q: q || '', limitPerType: 6 };
    this.applyScope(body);

    const pageNeedle = (q || '').trim();
    const pageGroup: GlobalSearchGroup = {
      type: 'page',
      label: 'Pages & Settings',
      total: 0,
      items: filterGlobalSearchPages(pageNeedle, 8)
    };
    pageGroup.total = pageGroup.items.length;

    if (!pageNeedle) {
      return this.dataService.getUserData('admin/globalSearch/search', body, token).pipe(
        map((res: any) => this.mergePages(res, pageGroup, true)),
        catchError(() => of({ q: '', groups: pageGroup.items.length ? [pageGroup] : [] }))
      );
    }

    if (/^p:\s*/i.test(pageNeedle)) {
      return of({ q: pageNeedle, groups: pageGroup.items.length ? [pageGroup] : [] });
    }

    return this.dataService.getUserData('admin/globalSearch/search', body, token).pipe(
      map((res: any) => this.mergePages(res, pageGroup, false)),
      catchError(() => of({
        q: pageNeedle,
        groups: pageGroup.items.length ? [pageGroup] : []
      }))
    );
  }

  advancedQuery(filters: Record<string, any>): Observable<GlobalSearchAdvancedResponse> {
    const token = this.dataService.getUserToken();
    const body: any = { ...(filters || {}) };
    this.applyScope(body);
    return this.dataService.getUserData('admin/globalSearch/query', body, token).pipe(
      map((res: any) => {
        const data = (res && res.Data) || {};
        return {
          page: data.page || 1,
          limit: data.limit || 25,
          total: data.total || 0,
          tookMs: data.tookMs,
          items: Array.isArray(data.items) ? data.items : [],
          hasMore: !!data.hasMore
        } as GlobalSearchAdvancedResponse;
      }),
      catchError(() => of({
        page: 1,
        limit: 25,
        total: 0,
        items: [],
        hasMore: false
      }))
    );
  }

  suggest(q: string, types?: string[]): Observable<GlobalSearchSuggestion[]> {
    const token = this.dataService.getUserToken();
    const body: any = { q: q || '', types: types || ['subscriber', 'property', 'report'], limit: 8 };
    this.applyScope(body);
    return this.dataService.getUserData('admin/globalSearch/suggest', body, token).pipe(
      map((res: any) => {
        const data = (res && res.Data) || {};
        return Array.isArray(data.suggestions) ? data.suggestions : [];
      }),
      catchError(() => of([]))
    );
  }

  listSaved(): Observable<GlobalSearchSavedItem[]> {
    const token = this.dataService.getUserToken();
    return this.dataService.getUserData('admin/globalSearch/saved/list', {}, token).pipe(
      map((res: any) => Array.isArray(res && res.Data) ? res.Data : []),
      catchError(() => of([]))
    );
  }

  saveSearch(name: string, filters: Record<string, any>, id?: number): Observable<GlobalSearchSavedItem | null> {
    const token = this.dataService.getUserToken();
    const body: any = { name, filters };
    if (id) {
      body.id = id;
    }
    return this.dataService.getUserData('admin/globalSearch/saved/save', body, token).pipe(
      map((res: any) => (res && res.Data) || null),
      catchError(() => of(null))
    );
  }

  deleteSaved(id: number): Observable<boolean> {
    const token = this.dataService.getUserToken();
    return this.dataService.getUserData('admin/globalSearch/saved/delete', { id }, token).pipe(
      map((res: any) => !!(res && res.Status === 200)),
      catchError(() => of(false))
    );
  }

  getRecent(): string[] {
    try {
      const raw = localStorage.getItem(this.recentKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }

  rememberRecent(q: string) {
    const term = (q || '').trim();
    if (!term) {
      return;
    }
    const next = [term, ...this.getRecent().filter((x) => x.toLowerCase() !== term.toLowerCase())].slice(0, 8);
    localStorage.setItem(this.recentKey, JSON.stringify(next));
  }

  clearRecent() {
    localStorage.removeItem(this.recentKey);
  }

  private mergePages(res: any, pageGroup: GlobalSearchGroup, emptyQuery: boolean): GlobalSearchResponse {
    const data = (res && res.Data) || { q: '', groups: [] };
    const groups: GlobalSearchGroup[] = Array.isArray(data.groups) ? [...data.groups] : [];
    if (pageGroup.items.length) {
      if (emptyQuery) {
        groups.unshift(pageGroup);
      } else {
        groups.push(pageGroup);
      }
    }
    return {
      q: data.q || '',
      tookMs: data.tookMs,
      groups
    };
  }

  private applyScope(body: any) {
    const scope = this.getSubscriberScope();
    if (scope) {
      body.scopeSubscriber = true;
      body.scopedSubscriberId = scope;
    }
  }

  private getSubscriberScope(): number | null {
    try {
      if (sessionStorage.getItem('userLogin')) {
        return null;
      }
      const sub = JSON.parse(sessionStorage.getItem('subscriberLogin') || 'null');
      if (!sub || !sub.Data) {
        return null;
      }
      if (sub.token && sub.Data.subscriberId) {
        return Number(sub.Data.subscriberId);
      }
      return Number(sub.Data.id) || null;
    } catch {
      return null;
    }
  }
}
